// Widget ECharts. Dos modos:
//  - declarativo (default): arma una option base desde (source + chart + encoding)
//    leyendo data[source] y deep-mergea block.option encima.
//  - custom (block.mode === 'custom'): ejecuta la FUNCIÓN del analista
//    `(data, theme, helpers) => option` (modo Código del builder). Recibe el
//    payload COMPLETO en `data` para que pueda combinar cualquier fuente.
// Si el código falla (compilar o ejecutar), muestra una tarjeta de error sin
// tumbar el resto del tablero.
//
// Datos por-widget (Fase 4): si el widget tiene data_sql materializada (filas
// PLANAS en block._widgetData.rows), 'line' y 'gauge' la usan directo; para
// multiLine/heatmap (estructura anidada {dates,series}/{departments,...}) ese SQL
// se consume desde el modo Código (reshape), no en declarativo.
import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import {
  buildLineChartWithMarkLine,
  buildMultiLineChart,
  buildHeatmapMatrix,
  buildKPIGauge,
} from '../echarts/options';
import { deepMerge } from '../echarts/deepMerge';
import { runChartCode } from '../echarts/customRuntime';

const BUILDERS = {
  line: (data, theme, enc) => buildLineChartWithMarkLine(data || [], theme, enc),
  multiLine: (data, theme, enc) => buildMultiLineChart(data || { dates: [], series: [] }, theme, enc),
  heatmap: (data, theme, enc) => buildHeatmapMatrix(data || { departments: [], retailers: [], cells: {} }, theme, enc),
  // gauge lee un KPI escalar de data.kpis[enc.metric], no data[source].
  gauge: (kpiData, theme, enc) => buildKPIGauge(kpiData, theme, enc),
};

// Altura por tipo de gráfica (alineada a las clases vz-chart-body actuales).
const HEIGHTS = { line: 320, multiLine: 420, heatmap: 580, gauge: 300 };

function ChartShell({ title, theme, height, children }) {
  return (
    <div className="vz-card" style={{ height: '100%' }}>
      {title && <div className="vz-card-title" style={{ background: theme.primary }}>{title}</div>}
      <div className="vz-chart-body" style={{ height }}>{children}</div>
    </div>
  );
}

export default function EChartsWidget({ block, data, theme }) {
  const { source, chart = 'line', encoding = {}, option: override, title, mode, code } = block;
  const isCustom = mode === 'custom';
  // Data materializada por-widget (Fase 4). Si existe, manda sobre el payload compartido.
  const widgetRows = block._widgetData?.rows;

  // MODO CÓDIGO: ejecuta la función del analista. Recibe el payload compartido +
  // (si existe) las filas de SU data_sql en `data.rows`.
  const custom = useMemo(() => {
    if (!isCustom || !code) return null;
    const payload = widgetRows ? { ...data, rows: widgetRows } : data;
    return runChartCode(code, payload, theme);
  }, [isCustom, code, data, theme, widgetRows]);

  // MODO DECLARATIVO: builders config-driven (con fallback a la data del widget).
  const option = useMemo(() => {
    if (isCustom) return null;
    const builder = BUILDERS[chart];
    if (!builder) return null;
    let base;
    if (chart === 'gauge') {
      const current = widgetRows && widgetRows.length
        ? widgetRows[0]?.[encoding.metric]
        : data?.kpis?.[encoding.metric];
      base = builder({ current, format: encoding.format, ...encoding, title }, theme, encoding);
    } else {
      // Filas planas solo encajan en 'line'; multiLine/heatmap caen al payload
      // compartido (su data_sql se usa desde el modo Código).
      const srcData = (widgetRows && chart === 'line') ? widgetRows : data?.[source];
      base = builder(srcData, theme, encoding);
    }
    return override ? deepMerge(base, override) : base;
  }, [isCustom, chart, source, encoding, override, data, theme, title, widgetRows]);

  const height = HEIGHTS[chart] || (isCustom ? 420 : 340);

  if (isCustom) {
    if (!code) {
      return <div className="vz-card vz-empty">Widget en modo Código sin código. Edítalo para agregarlo.</div>;
    }
    if (custom?.error) {
      return (
        <div className="vz-card vz-card--code-error">
          {title && <div className="vz-card-title" style={{ background: theme.primary }}>{title}</div>}
          <div className="vz-code-error">
            <strong>Error de {custom.errorType === 'compile' ? 'compilación' : 'ejecución'}</strong>
            <pre>{custom.error}</pre>
          </div>
        </div>
      );
    }
    return (
      <ChartShell title={title} theme={theme} height={height}>
        <ReactECharts
          option={custom.option}
          notMerge={true}
          lazyUpdate={true}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      </ChartShell>
    );
  }

  if (!option) {
    return <div className="vz-card vz-empty">Chart ECharts desconocido: {chart}</div>;
  }

  return (
    <ChartShell title={title} theme={theme} height={height}>
      <ReactECharts
        option={option}
        notMerge={true}
        lazyUpdate={true}
        style={{ height: '100%', width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </ChartShell>
  );
}
